import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { ReactComponent as PaymentTypeModal } from '../../../../assets/images/PaymentTypeMethodsModal.svg';
import { ReactComponent as PaymentTypeModalDark } from '../../../../assets/images/PaymentTypeMethodsModalDark.svg';
import { PaymentTypeItem } from './PaymentTypeItem';
import { InactiveTypeItem } from './InactiveTypeItem';
import { useAppSelector, useAppDispatch } from '../../../../hooks/redux';
import {
  LocalStorageItems,
  UnknownCardSystem,
  GlobalRouteNames,
  SuccessRouteNames,
  TopUpRouteNames,
  ButtonClassNames,
  ModalVariants,
} from '../../../../models/Common';
import { usePlaton } from '../../../../hooks/usePlaton';
import { openInSameTab } from '../../../../services/domService';
import { useCustomSnackbar } from '../../../../hooks/useCustomEnqueueSnackbar';
import { paymentsApi, GooglePayPlatonData } from '../../../../services/apiServices/user/payments';
import { OrderParams, PreparedCalculateData } from '../../../../models/Calculate';
import { modalSlice } from '../../../../store/reducers/ModalSlice';
import { paymentsCardsApi } from '../../../../services/apiServices/user/paymentsCards';
import { CustomNumberFormat } from '../../CustomNumberFormat';
import { applePayIdAvailableFor, Callbacks, googlePayIdAvailableFor } from '../../../../constants/configuration';
import { isSystemPaymentAvailable, getErrorMessage } from '../../../../utils';
import { CustomGooglePayButton } from '../../../paymentTypes/CustomGooglePayButton';
import { CustomMUIButton } from '../../buttons/CustomMUIButton';
import { SavePaymentCard } from '../../../paymentTypes/SavePaymentCard';
import { DataLayerMethods } from '../../../../types/dataLayerTypes';

interface PaymentTypeMethodsProps {
  currentCalculation: PreparedCalculateData | null;
  paramsForCalculation: OrderParams;
  serviceName: string;
  topUpAmount: number;
  formTarget: 'payForService' | 'topUpBalance';
  refScrollToTop: React.RefObject<HTMLDivElement>;
  bannerId?: number;
}

const errorUrl = `${process.env.REACT_APP_HOME || ''}${GlobalRouteNames.TopUpBalancePage}${TopUpRouteNames.BankCard}`;
const successUrl = `${process.env.REACT_APP_HOME || ''}${GlobalRouteNames.SuccessPage}${
  SuccessRouteNames.PaymentSuccessfulPage
}`;

export const PaymentTypeMethods: FC<PaymentTypeMethodsProps> = ({
  currentCalculation,
  paramsForCalculation,
  serviceName,
  topUpAmount,
  bannerId,
  formTarget,
  refScrollToTop,
}) => {
  const { t } = useTranslation();
  const { theme } = useAppSelector((state) => state.appReducer);
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { data: listOfCardsAPI, refetch: refetchListOfCards } = paymentsCardsApi.useGetListOfCardsQuery('');
  const [createServiceOrderAPI, { isLoading: isCreatingOrder }] = paymentsApi.useCreateServiceOrderMutation();
  const [googlePay] = paymentsApi.useGooglePayMutation();
  const { platonData, setPlatonData, getLinkToPlatonFormAPI, isPlatonLoading, reFetchPlatonAPI, isPlatonError } =
    usePlaton();
  const { userInfoState } = useAppSelector((state) => state.userInfoReducer);
  const [isLoading, setIsLoading] = useState(false);
  const isGooglePaymentAvailable = isSystemPaymentAvailable(googlePayIdAvailableFor, userInfoState.id);
  const isApplePaymentAvailable = isSystemPaymentAvailable(applePayIdAvailableFor, userInfoState.id);
  const isCardListExists = listOfCardsAPI && listOfCardsAPI.list.length > 0;
  const activeCardsCount = listOfCardsAPI && listOfCardsAPI.list.filter((i) => i.Status === 'active').length;
  const [step, setStep] = useState(0);
  const [selectedCard, setSelectedCard] = useState<number | undefined>();
  const blockRef = useRef<HTMLDivElement>(null);
  const isPayForService = formTarget === 'payForService';
  const isTopUpBalance = formTarget === 'topUpBalance';
  const isSubmitting = isCreatingOrder || isPlatonLoading;

  /* Показывать или нет блок с кнопками GPay и ApplePay */
  const isPaymentSystemsAvailable = true || isGooglePaymentAvailable || isApplePaymentAvailable;

  const handleServicePay = (amount: number, code: string, cardId?: number, saveCard?: boolean | number) => {
    if (!currentCalculation) return;
    setPlatonData({
      ...platonData,
      amount,
      order: code,
      description: serviceName,
      cardId,
      saveCard,
      ...(bannerId ? { promoBannerId: bannerId.toString() } : {}),
    });
    localStorage.setItem(LocalStorageItems.ServiceName, serviceName);
    localStorage.setItem(
      LocalStorageItems.FromDate,
      DateTime.fromSeconds(currentCalculation.view.fts).setZone('Europe/Kiev').toJSDate().toString(),
    );
    localStorage.setItem(
      LocalStorageItems.ToDate,
      DateTime.fromSeconds(currentCalculation.view.tts).setZone('Europe/Kiev').toJSDate().toString(),
    );
  };

  const handlePay = async (cardId?: number, saveCard?: boolean | number) => {
    if (isPayForService) {
      try {
        await createServiceOrderAPI(paramsForCalculation)
          .unwrap()
          .then((resp) => {
            handleServicePay(resp.Amount, resp.Code, cardId, saveCard);
            reFetchPlatonAPI({
              ...platonData,
              amount: resp.Amount,
              order: resp.Code,
              description: serviceName,
              cardId,
              saveCard,
              ...(bannerId ? { promoBannerId: bannerId.toString() } : {}),
            });
          });
      } catch (err) {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
    if (isTopUpBalance) {
      setPlatonData({
        amount: topUpAmount,
        url: successUrl,
        error_url: errorUrl,
        source: process.env.REACT_APP_HOME || '',
        order: '',
        description: serviceName,
        cardId,
        saveCard,
        ...(bannerId ? { promoBannerId: bannerId.toString() } : {}),
      });
      reFetchPlatonAPI({
        amount: topUpAmount,
        url: successUrl,
        error_url: errorUrl,
        source: process.env.REACT_APP_HOME || '',
        order: '',
        description: serviceName,
        cardId,
        saveCard,
        ...(bannerId ? { promoBannerId: bannerId.toString() } : {}),
      });
    }
  };

  const redirectPost = (url, data) => {
    const form = document.createElement('form');
    document.body.appendChild(form);
    form.method = 'post';
    form.action = url;
    Object.keys(data).forEach((name) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = data[name];
      form.appendChild(input);
    });
    form.submit();
  };

  const handleGooglePay = async (token: string, email: string) => {
    try {
      if (!currentCalculation) return;
      setIsLoading(true);

      const googlePayOptions: Partial<GooglePayPlatonData> = {
        url: Callbacks.PaymentError,
        source: process.env.REACT_APP_HOME || '',
        description: serviceName,
        token,
        email,
      };

      if (isPayForService) {
        const order = await createServiceOrderAPI(paramsForCalculation).unwrap();
        googlePayOptions.amount = order.Amount;
        googlePayOptions.order = order.Code;
        localStorage.setItem(LocalStorageItems.ServiceName, serviceName);
        localStorage.setItem(
          LocalStorageItems.FromDate,
          DateTime.fromSeconds(currentCalculation.view.fts).setZone('Europe/Kiev').toJSDate().toString(),
        );
        localStorage.setItem(
          LocalStorageItems.ToDate,
          DateTime.fromSeconds(currentCalculation.view.tts).setZone('Europe/Kiev').toJSDate().toString(),
        );
      }
      if (isTopUpBalance) {
        googlePayOptions.amount = topUpAmount;
        googlePayOptions.order = '';
      }

      const result = await googlePay(googlePayOptions as GooglePayPlatonData).unwrap();
      if (result.redirect_method === 'GET') {
        openInSameTab(result.redirect_url);
      } else if (result.redirect_method === 'POST') {
        redirectPost(result.redirect_url, result.redirect_params);
      }
      setIsLoading(false);
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (getLinkToPlatonFormAPI) {
      openInSameTab(getLinkToPlatonFormAPI.short_link);
      localStorage.setItem(LocalStorageItems.TopUpAmount, `${platonData.amount}`);
      if (isTopUpBalance) {
        localStorage.setItem(LocalStorageItems.TopUpBalance, `${DataLayerMethods.BANK_CARD}`);
      }
    }
  }, [getLinkToPlatonFormAPI]);

  const scrollToTop = () => {
    if (refScrollToTop.current && blockRef.current) {
      const { height } = blockRef.current.getBoundingClientRect();
      if (height > window.innerHeight) {
        refScrollToTop.current.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }
  };

  const handleOpenBlock = () => {
    setStep((prevState) => {
      return prevState + 1;
    });
    scrollToTop();
  };

  useEffect(() => {
    if (isPlatonError) {
      refetchListOfCards();
    }
  }, [isPlatonError]);

  useEffect(() => {
    if (activeCardsCount) {
      setSelectedCard(listOfCardsAPI.list.find((i) => i.Status === 'active')?.id);
    } else {
      setSelectedCard(undefined);
    }
  }, [activeCardsCount]);

  const payButtonHandler = () => {
    if (selectedCard) {
      handlePay(selectedCard);
    } else {
      handleOpenBlock();
    }
  };

  const getNewCardText = () => {
    let output;

    if (isCardListExists) {
      if (isPayForService) {
        output = t('servicePayment.payWithAnotherCard');
      }
      if (isTopUpBalance) {
        output = t('servicePayment.topUpWithAnotherCard');
      }
    } else {
      if (isPayForService) {
        output = t('servicePayment.payWithCard');
      }
      if (isTopUpBalance) {
        output = t('servicePayment.topUpWithCard');
      }
    }
    return output;
  };

  const handleDeleteCard = (cardId: string, cardNumber: string) => {
    dispatch(openModal({ modalVariants: ModalVariants.DeleteCard, content: { cardId, cardNumber } }));
  };

  const handleSelectCard = (cardId?: number) => {
    setSelectedCard(cardId);
  };

  return (
    <div className="modals payment-types" ref={blockRef}>
      <div className="payment__type__container">
        <div className="payment__type__container__img">
          {theme === 'light' ? <PaymentTypeModal /> : <PaymentTypeModalDark />}
        </div>
        <div className="payment__type__container__content">
          <div className={`payment__type__container__content__payment-method ${step !== 0 ? 'slideOut' : ''}`}>
            <div className="payment__type__container__content__payment-method__title">
              <h3>
                {isPayForService && t('servicePayment.selectPaymentMethod')}
                {isTopUpBalance && t('servicePayment.selectTopUpMethod')}
              </h3>
            </div>
            <div className="payment__type__container__content__payment-method__list">
              {listOfCardsAPI &&
                listOfCardsAPI.list.map((card) => {
                  if (card.Status === 'suspended') {
                    return (
                      <InactiveTypeItem key={card.id} theme={theme} typeMethod={card} handleDelete={handleDeleteCard} />
                    );
                  }
                  return (
                    <PaymentTypeItem
                      cardId={card.id}
                      typeMethod={card}
                      selectedCard={selectedCard}
                      theme={theme}
                      handleSelect={handleSelectCard}
                      key={card.id}
                    />
                  );
                })}
              <PaymentTypeItem
                typeMethod={UnknownCardSystem.NEW_CARD_DEFAULT}
                theme={theme}
                selectedCard={selectedCard}
                handleSelect={handleSelectCard}
                newCardText={getNewCardText()}
              />
              <hr />
              {!isPaymentSystemsAvailable && (
                <>
                  {isPayForService && (
                    <CustomGooglePayButton
                      isLoading={isLoading}
                      isGooglePaymentAvailable={isGooglePaymentAvailable}
                      gatewayMerchantId={userInfoState?.Entrepreneur?.PlatonKey ?? ''}
                      currentTotalPrice={`${currentCalculation?.pay_amount.toFixed(2)}`}
                      googlePay={handleGooglePay}
                    />
                  )}
                  {isTopUpBalance && (
                    <CustomGooglePayButton
                      isLoading={isLoading}
                      isGooglePaymentAvailable={isGooglePaymentAvailable}
                      gatewayMerchantId={userInfoState?.Entrepreneur?.PlatonKey ?? ''}
                      currentTotalPrice={`${topUpAmount.toFixed(2)}`}
                      googlePay={handleGooglePay}
                    />
                  )}
                  {/*
                TODO: ниже необходимо будет реализовать кнопку для оплаты с помощью ApplePay
                {isApplePaymentAvailable ? () : null}
                */}
                  {isPaymentSystemsAvailable ? <hr /> : null}
                </>
              )}
            </div>
            <div className="payment__type__container__content__payment-method__amount">
              <div className="payment__type__container__content__payment-method__amount__value">
                <span className="to-pay">
                  {isPayForService && t('servicePayment.sum')}
                  {isTopUpBalance && t('paymentHistory.paySum')}
                </span>
                <span className="total">
                  {isPayForService && (
                    <CustomNumberFormat
                      value={
                        currentCalculation && currentCalculation.pay_amount > 1 ? currentCalculation?.pay_amount : 1
                      }
                      suffix={t('common.uah')}
                    />
                  )}
                  {isTopUpBalance && (
                    <CustomNumberFormat value={topUpAmount > 1 ? topUpAmount : 1} suffix={t('common.uah')} />
                  )}
                </span>
              </div>
              {isPayForService && currentCalculation && currentCalculation.pay_amount < 1 ? (
                <div className="payment__type__container__content__payment-method__amount__info">
                  <span>{t('servicePayment.minimumPaymentSum')}</span>
                </div>
              ) : null}
            </div>
            <hr />
            <CustomMUIButton
              label={t('buttons.continue')}
              handleClick={payButtonHandler}
              className={ButtonClassNames.PrimaryFulfilled}
              isLoading={isSubmitting}
              isBold
            />
          </div>
          <SavePaymentCard handlePay={handlePay} step={step} />
        </div>
      </div>
    </div>
  );
};
